import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  Plus,
  Building2,
  Briefcase,
  Heart,
  Landmark,
  BarChart3,
  ClipboardList,
  Clock,
  Download,
  Eye,
  Edit,
  Trash2,
  Copy,
  MoreVertical,
  Search,
  Filter,
  Sparkles,
  Share2,
  Users,
  Globe
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../ui/tabs';
import ShareTemplateModal from './ShareTemplateModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Preset template configurations
const PRESET_TEMPLATES = [
  {
    id: 'bank',
    name: 'Bank Loan',
    description: 'Complete loan application package',
    icon: Building2,
    color: 'bg-blue-500',
    audience: 'bank'
  },
  {
    id: 'investor',
    name: 'Investor Pitch',
    description: 'Compelling investor presentation',
    icon: Briefcase,
    color: 'bg-purple-500',
    audience: 'investor'
  },
  {
    id: 'donor',
    name: 'Donor Report',
    description: 'Impact-focused donor report',
    icon: Heart,
    color: 'bg-pink-500',
    audience: 'donor'
  },
  {
    id: 'government',
    name: 'Government',
    description: 'Regulatory submission format',
    icon: Landmark,
    color: 'bg-amber-500',
    audience: 'government'
  },
  {
    id: 'board',
    name: 'Board Report',
    description: 'Quarterly board presentation',
    icon: BarChart3,
    color: 'bg-green-500',
    audience: 'board'
  },
  {
    id: 'internal',
    name: 'Internal',
    description: 'Detailed internal planning',
    icon: ClipboardList,
    color: 'bg-gray-500',
    audience: 'internal'
  }
];

// Audience badge colors
const AUDIENCE_COLORS = {
  bank: 'bg-blue-100 text-blue-800',
  investor: 'bg-purple-100 text-purple-800',
  donor: 'bg-pink-100 text-pink-800',
  government: 'bg-amber-100 text-amber-800',
  board: 'bg-green-100 text-green-800',
  internal: 'bg-gray-100 text-gray-800',
  custom: 'bg-slate-100 text-slate-800'
};

const ReportCenter = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  // State
  const [templates, setTemplates] = useState([]);
  const [sharedWithMe, setSharedWithMe] = useState([]);
  const [presets, setPresets] = useState([]);
  const [recentReports, setRecentReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteTemplate, setDeleteTemplate] = useState(null);
  const [audienceFilter, setAudienceFilter] = useState('all');
  const [shareTemplate, setShareTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState('my-templates');

  // Fetch templates and reports
  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch user templates
      const templatesRes = await fetch(`${BACKEND_URL}/api/report-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const templatesData = await templatesRes.json();
      setTemplates(templatesData.templates || []);

      // Fetch templates shared with me
      const sharedRes = await fetch(`${BACKEND_URL}/api/report-templates/shared-with-me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (sharedRes.ok) {
        const sharedData = await sharedRes.json();
        setSharedWithMe(sharedData.templates || []);
      }

      // Fetch presets
      const presetsRes = await fetch(`${BACKEND_URL}/api/report-templates/presets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const presetsData = await presetsRes.json();
      setPresets(presetsData.presets || []);

      // Fetch recent reports
      const reportsRes = await fetch(`${BACKEND_URL}/api/report-templates/reports/list?limit=5`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const reportsData = await reportsRes.json();
      setRecentReports(reportsData.reports || []);

      // Fetch stats
      const statsRes = await fetch(`${BACKEND_URL}/api/report-templates/stats/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const statsData = await statsRes.json();
      setStats(statsData);

    } catch (error) {
      console.error('Error fetching report data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle template deletion
  const handleDeleteTemplate = async (templateId) => {
    try {
      await fetch(`${BACKEND_URL}/api/report-templates/${templateId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(templates.filter(t => t.id !== templateId));
      setDeleteTemplate(null);
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  // Handle template clone
  const handleCloneTemplate = async (templateId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/report-templates/${templateId}/clone`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      const data = await res.json();
      if (data.template) {
        setTemplates([data.template, ...templates]);
      }
    } catch (error) {
      console.error('Error cloning template:', error);
    }
  };

  // Filter templates
  const filteredTemplates = templates.filter(t => {
    const matchesSearch = t.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesAudience = audienceFilter === 'all' || t.audience === audienceFilter;
    return matchesSearch && matchesAudience;
  });

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <FileText className="h-8 w-8 text-blue-600" />
              Report Center
            </h1>
            <p className="text-gray-500 mt-1">
              Create custom reports tailored to your audience
            </p>
          </div>
          <Button 
            onClick={() => navigate('/reports/builder')}
            className="mt-4 md:mt-0 bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Template
          </Button>
        </div>

        {/* Quick Generate Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-500" />
              Quick Generate
            </CardTitle>
            <CardDescription>
              Click a card to instantly generate a professional report
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {PRESET_TEMPLATES.map((preset) => {
                const Icon = preset.icon;
                return (
                  <button
                    key={preset.id}
                    onClick={() => navigate(`/reports/builder?preset=${preset.id}`)}
                    className="group relative flex flex-col items-center p-4 rounded-xl border-2 border-gray-200 bg-white hover:border-blue-500 hover:shadow-lg transition-all duration-200"
                  >
                    <div className={`w-12 h-12 rounded-xl ${preset.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <span className="font-medium text-gray-900 text-sm">
                      {preset.name}
                    </span>
                    <span className="text-xs text-gray-500 text-center mt-1 line-clamp-2">
                      {preset.description}
                    </span>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Templates Section with Tabs */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <TabsList>
                    <TabsTrigger value="my-templates" className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      My Templates
                      {templates.length > 0 && (
                        <Badge variant="secondary" className="ml-1">{templates.length}</Badge>
                      )}
                    </TabsTrigger>
                    <TabsTrigger value="shared-with-me" className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Shared with Me
                      {sharedWithMe.length > 0 && (
                        <Badge variant="secondary" className="ml-1">{sharedWithMe.length}</Badge>
                      )}
                    </TabsTrigger>
                  </TabsList>
                  <div className="flex gap-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Search templates..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 w-64"
                      />
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="icon">
                          <Filter className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setAudienceFilter('all')}>
                          All Audiences
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setAudienceFilter('bank')}>Bank</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setAudienceFilter('investor')}>Investor</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setAudienceFilter('donor')}>Donor</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setAudienceFilter('government')}>Government</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setAudienceFilter('internal')}>Internal</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </Tabs>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              {/* My Templates Tab */}
              <TabsContent value="my-templates">
                {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="p-4 border rounded-lg">
                    <Skeleton className="h-6 w-3/4 mb-2" />
                    <Skeleton className="h-4 w-1/2 mb-4" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                ))}
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-1">
                  No templates yet
                </h3>
                <p className="text-gray-500 mb-4">
                  Create your first custom report template or use a preset
                </p>
                <Button onClick={() => navigate('/reports/builder')}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Template
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredTemplates.map((template) => (
                  <div
                    key={template.id}
                    className="group p-4 border rounded-xl bg-white hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                          {template.name}
                        </h3>
                        <Badge className={`mt-1 ${AUDIENCE_COLORS[template.audience] || AUDIENCE_COLORS.custom}`}>
                          {template.audience}
                        </Badge>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/reports/builder/${template.id}`)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleCloneTemplate(template.id)}>
                            <Copy className="h-4 w-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setShareTemplate(template)}>
                            <Share2 className="h-4 w-4 mr-2" />
                            Share
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => setDeleteTemplate(template)}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    
                    <p className="text-sm text-gray-500 mb-3 line-clamp-2">
                      {template.description || 'No description'}
                    </p>
                    
                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <span>{template.blocks?.length || 0} blocks</span>
                      <span>Last used: {formatDate(template.last_used_at)}</span>
                    </div>
                    
                    <div className="flex gap-2 mt-4 pt-3 border-t">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => navigate(`/reports/builder/${template.id}`)}
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      <Button 
                        size="sm" 
                        className="flex-1 bg-blue-600 hover:bg-blue-700"
                        onClick={() => navigate(`/reports/generate/${template.id}`)}
                      >
                        Generate
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
              </TabsContent>

              {/* Shared with Me Tab */}
              <TabsContent value="shared-with-me">
                {loading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="p-4 border rounded-lg">
                        <Skeleton className="h-6 w-3/4 mb-2" />
                        <Skeleton className="h-4 w-1/2 mb-4" />
                        <Skeleton className="h-4 w-full" />
                      </div>
                    ))}
                  </div>
                ) : sharedWithMe.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-1">
                      No shared templates
                    </h3>
                    <p className="text-gray-500 mb-4">
                      Templates shared with you will appear here
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {sharedWithMe.map((template) => (
                      <div
                        key={template.id}
                        className="group p-4 border rounded-xl bg-white hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                              {template.name}
                            </h3>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge className={`${AUDIENCE_COLORS[template.audience] || AUDIENCE_COLORS.custom}`}>
                                {template.audience}
                              </Badge>
                              <div className="flex items-center gap-1 text-xs text-gray-500">
                                <Globe className="h-3 w-3" />
                                <span>by {template.owner_name}</span>
                              </div>
                            </div>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`/reports/builder/${template.id}`)}>
                                <Eye className="h-4 w-4 mr-2" />
                                View
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleCloneTemplate(template.id)}>
                                <Copy className="h-4 w-4 mr-2" />
                                Clone to My Templates
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                        
                        <p className="text-sm text-gray-500 mb-3 line-clamp-2">
                          {template.description || 'No description'}
                        </p>
                        
                        <div className="flex items-center justify-between text-xs text-gray-400">
                          <span>{template.blocks?.length || 0} blocks</span>
                          <span>Shared: {formatDate(template.shared_at)}</span>
                        </div>
                        
                        <div className="flex gap-2 mt-4 pt-3 border-t">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="flex-1"
                            onClick={() => navigate(`/reports/builder/${template.id}`)}
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            View
                          </Button>
                          <Button 
                            size="sm" 
                            className="flex-1 bg-blue-600 hover:bg-blue-700"
                            onClick={() => navigate(`/reports/generate/${template.id}`)}
                          >
                            Generate
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Recent Reports Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Recent Reports
            </CardTitle>
            <CardDescription>
              Your recently generated reports
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-14 w-full" />
                ))}
              </div>
            ) : recentReports.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No reports generated yet</p>
                <p className="text-sm mt-1">Generate a report from any template to see it here</p>
              </div>
            ) : (
              <div className="space-y-2">
                {recentReports.map((report) => (
                  <div
                    key={report.id}
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        report.format === 'pdf' ? 'bg-red-50' : 
                        report.format === 'xlsx' ? 'bg-green-50' : 'bg-blue-50'
                      }`}>
                        <FileText className={`h-5 w-5 ${
                          report.format === 'pdf' ? 'text-red-600' : 
                          report.format === 'xlsx' ? 'text-green-600' : 'text-blue-600'
                        }`} />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">
                          {report.name || report.template_name || 'Untitled Report'}
                        </h4>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span>{formatDate(report.generated_at)}</span>
                          <span>•</span>
                          <Badge variant="outline" className="text-xs">
                            {(report.format || 'pdf').toUpperCase()}
                          </Badge>
                          {report.page_count && (
                            <>
                              <span>•</span>
                              <span>{report.page_count} pages</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => navigate(`/reports/generate/${report.template_id}`)}
                        title="Regenerate"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => {
                          // Re-generate and download
                          navigate(`/reports/generate/${report.template_id}`);
                        }}
                        title="Download"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTemplate} onOpenChange={() => setDeleteTemplate(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deleteTemplate?.name}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeleteTemplate(deleteTemplate?.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share Template Modal */}
      <ShareTemplateModal
        isOpen={!!shareTemplate}
        onClose={() => setShareTemplate(null)}
        templateId={shareTemplate?.id}
        templateName={shareTemplate?.name}
      />
    </div>
  );
};

export default ReportCenter;
